import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import notify from "../../Notification/helper";
import ProductImageViewer from "../../../routes/pricing-reports/ProductImageViewer";
import LocationMissingBulkBar from "./LocationMissingBulkBar";
import LocationMissingToolbar from "./LocationMissingToolbar";
import {
  getCanonicalProvinceCodesSortedAlphabeticallyByDisplayName,
  provLabel,
} from "../PriceReports/provinceUtils";
import {
  bulkUpdatePriceHistoryLocationFields,
  fetchLocationMissingRows,
  fetchLocationMissingUploaderOptions,
  getDefaultLocationMissingDateFrom,
  getLocationMissingBulkUpdateRequestPreview,
  locationMissingReportConfigured,
} from "../../../services/locationMissingReport";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const LocationMissingReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateFrom, setDateFrom] = useState(getDefaultLocationMissingDateFrom());
  const [uploadedByKey, setUploadedByKey] = useState("");
  const [uploaderOptions, setUploaderOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRetailerId, setBulkRetailerId] = useState("");
  const [bulkRetailerName, setBulkRetailerName] = useState("");
  const [bulkProvince, setBulkProvince] = useState("");
  const [applying, setApplying] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);

  const provinceOptions = useMemo(
    () =>
      getCanonicalProvinceCodesSortedAlphabeticallyByDisplayName().map((code) => ({
        value: code,
        label: provLabel(code),
      })),
    []
  );

  const loadUploaderOptions = useCallback(async () => {
    if (!locationMissingReportConfigured()) {
      return;
    }

    try {
      const options = await fetchLocationMissingUploaderOptions({ dateFrom });
      setUploaderOptions(options);
    } catch (_) {
      setUploaderOptions([]);
    }
  }, [dateFrom]);

  const loadData = useCallback(async () => {
    if (!locationMissingReportConfigured()) {
      setError(
        "Location missing report is not configured. Set REACT_APP_PRICE_DIRECTUS_URL and REACT_APP_PRICE_DIRECTUS_TOKEN."
      );
      return;
    }

    setLoading(true);
    setError("");
    try {
      const result = await fetchLocationMissingRows({
        page: page + 1,
        limit: rowsPerPage,
        dateFrom,
        uploadedByKey,
      });
      setRows(result.rows);
      setTotal(result.total);
      if (uploadedByKey && result.uploaderOptions?.length) {
        setUploaderOptions(result.uploaderOptions);
      }
      setSelectedIds((prev) => prev.filter((id) => result.rows.some((row) => row.id === id)));
    } catch (e) {
      setError(e.message || "Failed to load location missing records.");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, dateFrom, uploadedByKey]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadUploaderOptions();
  }, [loadUploaderOptions]);

  const pageIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const somePageSelected = pageIds.some((id) => selectedIds.includes(id));

  const bulkUpdateRequestPreview = useMemo(() => {
    const payload = {};
    if (bulkRetailerId) {
      payload.retailerRefId = bulkRetailerId;
      payload.retailer = bulkRetailerName || bulkRetailerId;
    }
    if (bulkProvince) {
      payload.province = bulkProvince;
    }

    return getLocationMissingBulkUpdateRequestPreview(selectedIds, payload);
  }, [selectedIds, bulkRetailerId, bulkRetailerName, bulkProvince]);

  const toggleSelectAllOnPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }

    setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const toggleRowSelection = (rowId) => {
    setSelectedIds((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  };

  const handleApplyBulkUpdate = async () => {
    if (!selectedIds.length) {
      return;
    }

    const payload = {};
    if (bulkRetailerId) {
      payload.retailerRefId = bulkRetailerId;
      payload.retailer = bulkRetailerName || bulkRetailerId;
    }
    if (bulkProvince) {
      payload.province = bulkProvince;
    }

    if (!Object.keys(payload).length) {
      notify("error", "Choose a retailer and/or province before applying.");
      return;
    }

    setApplying(true);
    setError("");
    try {
      await bulkUpdatePriceHistoryLocationFields(selectedIds, payload);
      notify("success", `Updated ${selectedIds.length} record${selectedIds.length === 1 ? "" : "s"}.`);
      setSelectedIds([]);
      setBulkRetailerId("");
      setBulkRetailerName("");
      setBulkProvince("");
      await loadData();
      await loadUploaderOptions();
    } catch (e) {
      setError(e.message || "Failed to update selected records.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        boxShadow: "0 2px 4px rgba(0,0,0,0.5)",
        position: "relative",
        padding: "16px",
        backgroundColor: "#efeeee",
      }}
    >
      <Typography variant="h5" component="h2" sx={{ mb: 0.5 }}>
        Location / Missing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review recent price history rows whose original source image lacks EXIF GPS, then correct
        retailer and province in bulk.
      </Typography>

      {!locationMissingReportConfigured() ? (
        <Alert severity="warning">Beef AI Directus env vars are not configured for this report.</Alert>
      ) : null}

      <LocationMissingToolbar
        dateFrom={dateFrom}
        onDateFromChange={(nextDateFrom) => {
          setPage(0);
          setDateFrom(nextDateFrom || getDefaultLocationMissingDateFrom());
        }}
        uploaderOptions={uploaderOptions}
        uploadedByKey={uploadedByKey}
        onUploadedByKeyChange={(nextKey) => {
          setPage(0);
          setUploadedByKey(nextKey);
        }}
      />

      <LocationMissingBulkBar
        selectedCount={selectedIds.length}
        retailerId={bulkRetailerId}
        retailerName={bulkRetailerName}
        onRetailerIdChange={setBulkRetailerId}
        onRetailerNameChange={setBulkRetailerName}
        province={bulkProvince}
        onProvinceChange={setBulkProvince}
        provinceOptions={provinceOptions}
        bulkUpdateRequestPreview={bulkUpdateRequestPreview}
        onApply={handleApplyBulkUpdate}
        applying={applying}
        disabled={!locationMissingReportConfigured()}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ position: "relative" }}>
        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading records...</Typography>
          </Stack>
        ) : null}

        <Table
          sx={{
            width: "100%",
            border: "1px solid #eaeaea",
            backgroundColor: "#fff",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allPageSelected}
                  indeterminate={!allPageSelected && somePageSelected}
                  onChange={toggleSelectAllOnPage}
                  inputProps={{ "aria-label": "Select all rows on page" }}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Source image</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Retailer</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Province</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Uploaded by</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Image uploaded</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Record added</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover selected={selectedIds.includes(row.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.includes(row.id)}
                    onChange={() => toggleRowSelection(row.id)}
                    inputProps={{ "aria-label": `Select ${row.productName || row.id}` }}
                  />
                </TableCell>
                <TableCell>
                  {row.sourceImageUrl ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setPreviewRow(row)}
                      sx={{
                        border: 0,
                        padding: 0,
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        component="img"
                        src={row.sourceImageUrl}
                        alt={row.sourceFilename || "Source image"}
                        sx={{
                          width: 72,
                          height: 72,
                          objectFit: "cover",
                          borderRadius: 1,
                          border: "1px solid #ddd",
                          display: "block",
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {row.hasVerifiedSource ? "No preview" : "Unverified source"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{row.productName || "-"}</TableCell>
                <TableCell>{row.retailer || "-"}</TableCell>
                <TableCell>{row.province || "-"}</TableCell>
                <TableCell>
                  <Typography variant="body2">{row.uploaderName || "-"}</Typography>
                  {row.uploaderEmail ? (
                    <Typography variant="caption" color="text.secondary">
                      {row.uploaderEmail}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{formatDate(row.uploadedOn)}</TableCell>
                <TableCell>{formatDate(row.dateCreated)}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2">No records with missing EXIF location found.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Box>

      <Dialog
        open={Boolean(previewRow)}
        onClose={() => setPreviewRow(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Source image
          <IconButton
            aria-label="Close"
            onClick={() => setPreviewRow(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {previewRow?.sourceImageUrl ? (
            <Stack spacing={1.5}>
              <ProductImageViewer src={previewRow.sourceImageUrl} alt={previewRow.sourceFilename || "Source image"} />
              <Typography variant="body2" color="text.secondary">
                {previewRow.sourceFilename || previewRow.sourceFileId}
              </Typography>
              <Typography variant="body2">
                Uploaded by {previewRow.uploaderLabel} on {formatDate(previewRow.uploadedOn)}
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LocationMissingReport;
